export const platformHero = {
  title: 'Build Your Entire Smart Device Ecosystem on One Platform',
  subtitle:
    'From firmware to cloud infrastructure, mobile apps, analytics, and OTA updates — everything required to launch and scale a smart device brand.',
  primaryCta: { label: 'Book Live Demo', to: '/contact-us' },
  exploreAnchor: '#platform-architecture',
} as const;

export const platformArchitecture = {
  title: 'Platform Architecture',
  subtitle: 'A connected stack from device firmware to partner operations',
  blocks: [
    {
      id: 'firmware',
      title: 'Device Firmware',
      description: 'Production-ready SDKs for ESP32 and leading IoT chipsets.',
    },
    {
      id: 'cloud',
      title: 'IoT Cloud',
      description: 'Scalable MQTT and device twin infrastructure.',
    },
    {
      id: 'realtime',
      title: 'Real-Time Communication',
      description: 'Sub-second sync between devices, cloud, and apps.',
    },
    {
      id: 'analytics',
      title: 'Analytics Engine',
      description: 'Fleet-wide usage, health, and performance insights.',
    },
    {
      id: 'apps',
      title: 'White-Label Apps',
      description: 'Branded iOS and Android control experiences.',
    },
    {
      id: 'dashboard',
      title: 'Partner Dashboard',
      description: 'Centralized admin, tenants, and operations.',
    },
  ],
} as const;

export const platformModules = [
  {
    id: 'iot-cloud',
    title: 'IoT Cloud Platform',
    description:
      'Enterprise-grade cloud infrastructure built for millions of connected devices with real-time communication and auto-scaling architecture.',
    features: [
      'MQTT Infrastructure',
      'Real-Time Device Sync',
      'Device Twin Architecture',
      'Event Processing',
      'Auto Scaling',
      'Multi-Region Support',
    ],
  },
  {
    id: 'firmware-sdk',
    title: 'Device Firmware SDK',
    description:
      'Production-ready firmware SDKs and libraries for ESP32, ESP8266, and other IoT chipsets with secure provisioning and OTA support.',
    features: [
      'SmartConfig Provisioning',
      'AP Mode Setup',
      'OTA Updates',
      'Device Pairing',
      'Offline State Handling',
      'Secure Authentication',
    ],
  },
  {
    id: 'mobile-apps',
    title: 'White-Label Mobile Apps',
    description:
      'Launch your own branded iOS and Android apps with complete customization and real-time smart device control.',
    features: [
      'Custom Branding',
      'Dark Mode',
      'Push Notifications',
      'Real-Time Sync',
      'Device Sharing',
      'Scene Automation',
    ],
  },
  {
    id: 'partner-dashboard',
    title: 'Partner Dashboard',
    description:
      'Manage customers, devices, firmware versions, analytics, and support operations from one centralized dashboard.',
    features: [
      'Device Monitoring',
      'Usage Analytics',
      'Role Management',
      'Tenant Management',
      'Device Logs',
      'Remote Actions',
    ],
  },
] as const;

export const platformCapabilities = [
  { id: 'control', label: 'Real-Time Device Control' },
  { id: 'ota', label: 'OTA Firmware Updates' },
  { id: 'multi-tenant', label: 'Multi-Tenant Architecture' },
  { id: 'analytics', label: 'Device Analytics' },
  { id: 'provisioning', label: 'Device Provisioning' },
  { id: 'automation', label: 'Cloud Automation' },
  { id: 'rules', label: 'Rule Engine' },
  { id: 'push', label: 'Push Notifications' },
  { id: 'grouping', label: 'Device Grouping' },
  { id: 'scenes', label: 'Scene Automation' },
  { id: 'diagnostics', label: 'Remote Diagnostics' },
  { id: 'api', label: 'API Access' },
] as const;

export const platformEnterprise = {
  title: 'Enterprise Infrastructure Built for Scale',
  stats: [
    { value: '10M+', label: 'Device Capacity' },
    { value: '99.99%', label: 'Cloud Uptime' },
    { value: '<1s', label: 'MQTT Communication' },
    { value: 'Multi-Region', label: 'Deployment' },
  ],
  security: [
    'End-to-End Encryption',
    'Role-Based Access Control',
    'Secure Device Authentication',
    'GDPR Ready Infrastructure',
    'Audit Logs',
    'ISO Security Standards',
  ],
} as const;

export const platformApis = {
  title: 'Developer-Friendly APIs & Integrations',
  subtitle:
    'Flexible REST APIs, WebSocket infrastructure, and SDKs to integrate your products into any ecosystem.',
  integrations: [
    'AWS IoT',
    'Firebase',
    'Alexa',
    'Google Home',
    'MQTT APIs',
    'REST APIs',
    'Webhooks',
  ],
  snippets: [
    {
      id: 'rest',
      label: 'REST API',
      code: `GET /api/v1/devices/{deviceId}/state
Authorization: Bearer <partner_token>

{
  "deviceId": "plug-a1b2",
  "online": true,
  "power": "on",
  "updatedAt": "2026-05-17T10:42:00Z"
}`,
    },
    {
      id: 'mqtt',
      label: 'MQTT Subscribe',
      code: `// Real-time device events
mqtt.subscribe('tenants/{tenantId}/devices/+/events');

mqtt.on('message', (topic, payload) => {
  const event = JSON.parse(payload.toString());
  syncDashboard(event);
});`,
    },
  ],
} as const;

export const platformWorkflow = {
  title: 'Platform Workflow',
  subtitle: 'From firmware integration to fleet scale in five clear steps',
  steps: [
    { id: 1, title: 'Integrate Firmware', description: 'Ship devices with our SDK and secure provisioning.' },
    { id: 2, title: 'Connect Devices', description: 'Onboard fleets to cloud with MQTT and device twins.' },
    { id: 3, title: 'Customize Apps', description: 'Brand mobile apps and partner dashboard for your market.' },
    { id: 4, title: 'Launch Products', description: 'Go live with OTA, automation, and multi-tenant support.' },
    { id: 5, title: 'Monitor & Scale', description: 'Analytics, diagnostics, and regional expansion.' },
  ],
} as const;

export const platformDeviceManagement = {
  title: 'Device Management',
  subtitle: 'OTA updates, provisioning, and remote diagnostics for your entire fleet',
  points: [
    'Staged OTA rollouts with rollback',
    'SmartConfig and AP-mode provisioning',
    'Remote reboot, reset, and log collection',
  ],
} as const;

export const platformClosing = {
  titleLines: ['Ready to Build Your', 'Smart Device Platform?'],
  subtitle:
    'Launch faster with enterprise-grade IoT infrastructure designed for modern smart device brands.',
  demoCta: { label: 'Book Demo', to: '/contact-us' },
  partnerCta: { label: 'Become a Partner' },
} as const;

export const platformOverviewPremium = {
  eduIntro: {
    title: 'What is a connected device platform?',
    description:
      'A connected device platform unifies firmware, cloud, mobile apps, and operations—so you can launch, manage, and scale smart products without stitching together disparate vendors.',
  },
  featureShowcase: {
    eyebrow: 'PLATFORM STACK',
    title: 'Everything required to launch a smart device brand',
    subtitle: 'From silicon to partner dashboard in one integrated stack.',
    items: [
      {
        title: 'Device firmware SDK',
        description:
          'Production-ready libraries for ESP32 and leading chipsets with secure provisioning and OTA support.',
        image: { src: '/images/home/feature-firmware.jpg', alt: 'Firmware SDK', width: 1200, height: 800 },
        cta: { label: 'Explore firmware SDK', to: '/platform/firmware-sdk' },
      },
      {
        title: 'Enterprise IoT cloud',
        description:
          'MQTT infrastructure, device twins, and auto-scaling for millions of connected endpoints.',
        image: { src: '/images/home/feature-cloud.jpg', alt: 'IoT cloud', width: 1200, height: 800 },
        cta: { label: 'Explore IoT cloud', to: '/platform/iot-cloud' },
      },
      {
        title: 'White-label mobile apps',
        description:
          'Branded iOS and Android control experiences with scenes, automation, and real-time sync.',
        image: { src: '/images/home/white-label-app-image.png', alt: 'Mobile apps', width: 1200, height: 800 },
        cta: { label: 'Explore mobile apps', to: '/platform/mobile-apps' },
      },
    ],
  },
  ctaBanner: {
    title: 'Build your entire smart device ecosystem on one platform',
    ctaLabel: 'Get Started',
    ctaTo: '/contact-us',
    imageSrc: '/images/cta/solution-cta-robot.webp',
    imageAlt: 'Platform automation',
  },
  faqs: {
    title: 'Frequently asked questions',
    items: [
      {
        question: 'What does the platform include?',
        answer:
          'Firmware SDK, IoT cloud, white-label apps, partner dashboard, APIs, security, and device management—integrated end to end.',
      },
      {
        question: 'How quickly can we launch?',
        answer:
          'Most partners launch pilots in weeks using pre-built SDKs, cloud services, and branded app shells.',
      },
      {
        question: 'Is the platform white-label?',
        answer: 'Yes—your brand, colors, and customer experience across mobile apps and partner portals.',
      },
    ],
  },
} as const;
